import React, { useState, useEffect } from 'react';
import { Play, ChevronDown, ChevronRight, Clock, FileText } from 'lucide-react';
import {
  fetchRegulatoryDocuments,
  fetchContracts,
  startComplianceAnalysis,
  fetchComplianceFindings,
  fetchComplianceAnalysis,
  fetchComplianceAnalyses,
  type RegulatoryDocument,
  type ContractFromAPI,
  type ComplianceFinding,
  type ComplianceSummary,
  type ComplianceAnalysis,
} from '../../services/api';

interface RegulatoryComplianceViewProps {
  testId?: string;
}

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  met: { bg: 'bg-green-100', text: 'text-green-700', label: 'Met' },
  partially_met: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Partial' },
  not_met: { bg: 'bg-red-100', text: 'text-red-700', label: 'Not Met' },
  not_applicable: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'N/A' },
};

export const RegulatoryComplianceView: React.FC<RegulatoryComplianceViewProps> = ({ testId }) => {
  const [regulatoryDocs, setRegulatoryDocs] = useState<RegulatoryDocument[]>([]);
  const [contracts, setContracts] = useState<ContractFromAPI[]>([]);
  const [selectedDocId, setSelectedDocId] = useState<string>('');
  const [selectedContractId, setSelectedContractId] = useState<string>('');

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ComplianceSummary | null>(null);
  const [findings, setFindings] = useState<ComplianceFinding[]>([]);
  const [expandedFinding, setExpandedFinding] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  // Analysis history state
  const [pastAnalyses, setPastAnalyses] = useState<ComplianceAnalysis[]>([]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [activeAnalysisId, setActiveAnalysisId] = useState<string | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Load dropdown options + past analyses
  useEffect(() => {
    fetchRegulatoryDocuments()
      .then((docs) => setRegulatoryDocs(docs.filter((d) => d.status === 'requirements_extracted')))
      .catch(() => {});

    fetchContracts({ page: 1, limit: 100 })
      .then((response) => setContracts(response.contracts))
      .catch(() => {});

    loadPastAnalyses();
  }, []);

  const loadPastAnalyses = async () => {
    try {
      const analyses = await fetchComplianceAnalyses({ limit: 50 });
      setPastAnalyses(analyses);
    } catch {
      // Non-fatal
    }
  };

  const handleRunAnalysis = async () => {
    if (!selectedDocId || !selectedContractId) return;

    setIsAnalyzing(true);
    setAnalysisError(null);
    setSummary(null);
    setFindings([]);
    setActiveAnalysisId(null);

    try {
      const result = await startComplianceAnalysis(selectedDocId, selectedContractId);
      if (result.summary) {
        setSummary(result.summary);
      }
      if (result.analysisId) {
        setActiveAnalysisId(result.analysisId);
        const findingsData = await fetchComplianceFindings(result.analysisId);
        setFindings(findingsData);
      }
      // Refresh history list
      loadPastAnalyses();
    } catch (err) {
      setAnalysisError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleLoadAnalysis = async (analysis: ComplianceAnalysis) => {
    if (activeAnalysisId === analysis.id) return;

    setLoadingHistory(true);
    setAnalysisError(null);
    setSummary(null);
    setFindings([]);
    setExpandedFinding(null);
    setStatusFilter('');
    setCategoryFilter('');

    try {
      // Load summary from the analysis itself
      const fullAnalysis = await fetchComplianceAnalysis(analysis.id);
      if (fullAnalysis.summary) {
        setSummary(fullAnalysis.summary);
      }
      setActiveAnalysisId(analysis.id);

      // Set dropdowns to match
      setSelectedDocId(analysis.regulatory_doc_id);
      setSelectedContractId(analysis.contract_id);

      // Load findings
      const findingsData = await fetchComplianceFindings(analysis.id);
      setFindings(findingsData);
    } catch (err) {
      setAnalysisError(err instanceof Error ? err.message : 'Failed to load analysis');
    } finally {
      setLoadingHistory(false);
    }
  };

  const resolveContractName = (contractId: string) => {
    const contract = contracts.find((c) => c.id === contractId);
    return contract ? `${contract.name} (${contract.state})` : contractId.slice(0, 8) + '...';
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  // Filter findings
  const filteredFindings = findings.filter((f) => {
    if (statusFilter && f.compliance_status !== statusFilter) return false;
    if (categoryFilter && f.regulatory_requirements?.category !== categoryFilter) return false;
    return true;
  });

  const categories = Array.from(new Set(findings.map((f) => f.regulatory_requirements?.category).filter(Boolean))).sort();

  // Category breakdown
  const categoryBreakdown = categories.map((cat) => {
    const catFindings = findings.filter((f) => f.regulatory_requirements?.category === cat);
    const met = catFindings.filter((f) => f.compliance_status === 'met').length;
    const partial = catFindings.filter((f) => f.compliance_status === 'partially_met').length;
    const total = catFindings.filter((f) => f.compliance_status !== 'not_applicable').length;
    const score = total > 0 ? ((met + partial * 0.5) / total) * 100 : 0;
    return { category: cat, total: catFindings.length, met, partial, score };
  });

  return (
    <div className="space-y-6" data-testid={testId}>
      {/* Past Analyses */}
      {pastAnalyses.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <button
            onClick={() => setHistoryExpanded(!historyExpanded)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">
                Past Analyses ({pastAnalyses.length})
              </span>
            </div>
            {historyExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-400" />
            )}
          </button>

          {historyExpanded && (
            <div className="border-t border-gray-200 max-h-64 overflow-y-auto">
              {pastAnalyses.map((analysis) => {
                const isActive = activeAnalysisId === analysis.id;
                const docName = analysis.regulatory_documents?.name || 'Unknown document';
                const score = analysis.summary?.overall_score;

                return (
                  <button
                    key={analysis.id}
                    onClick={() => handleLoadAnalysis(analysis)}
                    disabled={loadingHistory}
                    className={`w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm transition-colors border-l-2 ${
                      isActive
                        ? 'bg-primary-50 border-l-primary-500'
                        : 'border-l-transparent hover:bg-gray-50'
                    } ${loadingHistory ? 'opacity-50 cursor-wait' : ''}`}
                  >
                    <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{docName}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {resolveContractName(analysis.contract_id)}
                      </p>
                    </div>
                    {score != null && (
                      <span className={`text-xs font-semibold flex-shrink-0 ${
                        score >= 80 ? 'text-green-600' : score >= 50 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {score}%
                      </span>
                    )}
                    <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">
                      {formatDate(analysis.created_at)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Selection Bar */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Regulatory Document</label>
            <select
              value={selectedDocId}
              onChange={(e) => setSelectedDocId(e.target.value)}
              className="w-full text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select document...</option>
              {regulatoryDocs.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({d.total_requirements} requirements)
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Contract</label>
            <select
              value={selectedContractId}
              onChange={(e) => setSelectedContractId(e.target.value)}
              className="w-full text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select contract...</option>
              {contracts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.state})
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleRunAnalysis}
            disabled={!selectedDocId || !selectedContractId || isAnalyzing}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isAnalyzing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Analyzing...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Run Analysis
              </>
            )}
          </button>
        </div>

        {analysisError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            {analysisError}
          </div>
        )}

        {regulatoryDocs.length === 0 && (
          <p className="mt-3 text-sm text-gray-500">
            No regulatory documents with extracted requirements found. Upload and process a document on the Regulations page first.
          </p>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-4">
          <SummaryCard label="Total" value={summary.total_requirements} color="bg-white" />
          <SummaryCard label="Met" value={summary.met} color="bg-green-50 border-green-200" textColor="text-green-700" />
          <SummaryCard label="Partial" value={summary.partially_met} color="bg-yellow-50 border-yellow-200" textColor="text-yellow-700" />
          <SummaryCard label="Not Met" value={summary.not_met} color="bg-red-50 border-red-200" textColor="text-red-700" />
          <SummaryCard label="N/A" value={summary.not_applicable} color="bg-gray-50 border-gray-200" textColor="text-gray-500" />
          <SummaryCard label="Score" value={`${summary.overall_score}%`} color="bg-primary-50 border-primary-200" textColor="text-primary-700" />
        </div>
      )}

      {/* Category Breakdown */}
      {categoryBreakdown.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-900 mb-4">Compliance by Category</h3>
          <div className="space-y-3">
            {categoryBreakdown.map(({ category, total, met, partial, score }) => (
              <div key={category} className="flex items-center gap-4">
                <span className="text-sm text-gray-700 w-56 truncate">{category}</span>
                <div className="flex-1 bg-gray-200 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="bg-primary-600 h-2.5 rounded-full transition-all"
                    style={{ width: `${Math.round(score)}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-gray-700 w-16 text-right">{Math.round(score)}%</span>
                <span className="text-xs text-gray-400 w-20">({met + partial}/{total})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Findings Table */}
      {findings.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Findings ({filteredFindings.length})
              </h3>
              <div className="flex gap-3">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">All Statuses</option>
                  <option value="met">Met</option>
                  <option value="partially_met">Partially Met</option>
                  <option value="not_met">Not Met</option>
                  <option value="not_applicable">Not Applicable</option>
                </select>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="text-sm border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">All Categories</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {filteredFindings.map((finding) => {
              const status = STATUS_COLORS[finding.compliance_status] || STATUS_COLORS.not_met;
              const isExpanded = expandedFinding === finding.id;
              const req = finding.regulatory_requirements;
              const matchedClauses = finding.matched_clauses || [];

              return (
                <div key={finding.id} className="hover:bg-gray-50">
                  <div
                    className="px-6 py-3 flex items-start gap-3 cursor-pointer"
                    onClick={() => setExpandedFinding(isExpanded ? null : finding.id)}
                  >
                    <span className="mt-0.5 text-gray-400">
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </span>
                    <span className="text-sm font-mono text-gray-500 w-20 flex-shrink-0">
                      {req?.requirement_number}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate">{req?.text}</p>
                    </div>
                    <span className="text-xs text-gray-500 flex-shrink-0">{req?.category}</span>
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full flex-shrink-0 ${status.bg} ${status.text}`}>
                      {status.label}
                    </span>
                    <span className="text-xs text-gray-400 flex-shrink-0 w-12 text-right">
                      {Math.round(finding.confidence * 100)}%
                    </span>
                  </div>

                  {isExpanded && (
                    <div className="px-6 pb-4 ml-10 space-y-3">
                      <div className="p-3 bg-gray-50 rounded text-sm space-y-2">
                        <p className="text-gray-800">
                          <span className="font-medium">Requirement:</span> {req?.text}
                        </p>
                        {finding.evidence_text && (
                          <p className="text-gray-700">
                            <span className="font-medium text-green-700">Evidence:</span> {finding.evidence_text}
                          </p>
                        )}

                        {/* Matched Contract Clauses */}
                        {matchedClauses.length > 0 && (
                          <div className="mt-2">
                            <p className="font-medium text-purple-700 mb-1.5">
                              Matched Contract Clauses ({matchedClauses.length}):
                            </p>
                            <div className="space-y-2">
                              {matchedClauses.map((clause) => {
                                const label = [
                                  clause.article_number ? `Art ${clause.article_number}` : null,
                                  clause.section_number ? `Sec ${clause.section_number}` : null,
                                ].filter(Boolean).join(' / ');

                                return (
                                  <div
                                    key={clause.id}
                                    className="border-l-2 border-purple-300 pl-3 py-1"
                                  >
                                    {label && (
                                      <span className="text-xs font-semibold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                                        {label}
                                      </span>
                                    )}
                                    <p className="text-gray-700 text-sm mt-0.5">
                                      {clause.text
                                        ? clause.text.length > 500
                                          ? clause.text.slice(0, 500) + '...'
                                          : clause.text
                                        : <span className="italic text-gray-400">Clause text unavailable</span>}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {finding.gap_description && (
                          <p className="text-gray-700">
                            <span className="font-medium text-red-700">Gap:</span> {finding.gap_description}
                          </p>
                        )}
                        {finding.recommendation && (
                          <p className="text-gray-700">
                            <span className="font-medium text-blue-700">Recommendation:</span> {finding.recommendation}
                          </p>
                        )}
                        {finding.reasoning && (
                          <p className="text-gray-500 text-xs">
                            <span className="font-medium">Reasoning:</span> {finding.reasoning}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Summary card sub-component
const SummaryCard: React.FC<{
  label: string;
  value: number | string;
  color: string;
  textColor?: string;
}> = ({ label, value, color, textColor = 'text-gray-900' }) => (
  <div className={`border rounded-lg p-4 ${color}`}>
    <p className="text-xs font-medium text-gray-500 uppercase">{label}</p>
    <p className={`mt-1 text-2xl font-bold ${textColor}`}>{value}</p>
  </div>
);
